<template>
  <v-app>
    <v-layout>
      <!-- Navigation Drawer -->
      <v-navigation-drawer
        v-model="drawer"
        app
        :permanent="mdAndUp"
        :temporary="!mdAndUp"
        :width="drawerWidth"
        class="nav-drawer"
      >
        <v-list-item
          title="SnapPoll Admin"
          subtitle="Management Portal"
          class="pa-3 px-4"
        >
          <template v-slot:prepend>
            <v-avatar color="#667eea" :size="avatarSize">
              <v-img src="/appIconWhite.png"></v-img>
            </v-avatar>
          </template>
        </v-list-item>

        <v-divider class="mt-n1"></v-divider>

        <v-list dense nav class="py-2">
          <v-list-item
            v-for="item in navItems"
            :key="item.path"
            :to="{ path: '/' + item.path }"
            :class="{ 'nav-item-active': isActive(item.path) }"
            :title="item.title"
            class="nav-item mb-2 mx-2"
            rounded="lg"
          >
            <template v-slot:prepend>
              <v-icon :icon="item.icon" size="small"></v-icon>
            </template>
          </v-list-item>
        </v-list>
        <template v-slot:append>
          <div class="pb-2">
            <v-divider class="divider-light mb-2"></v-divider>

            <v-list-item :title="userName" :subtitle="userEmail" disabled>
              <template v-slot:prepend>
                <v-avatar color="#764ba2" size="40">
                  <span class="text-white font-weight-bold">
                    {{ userInitial }}
                  </span>
                </v-avatar>
              </template>
            </v-list-item>

            <v-list-item
              title="Logout"
              class="nav-item mb-2 mx-2 text-error"
              @click="handleLogout"
              rounded="lg"
            >
              <template v-slot:prepend>
                <v-icon color="error" icon="mdi-logout" size="small"></v-icon>
              </template>
            </v-list-item>
          </div>
        </template>
      </v-navigation-drawer>

      <!-- App Bar -->
      <v-app-bar app elevation="3" :height="appBarHeight">
        <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>

        <v-toolbar-title
          :class="xs ? 'text-subtitle-2' : 'text-h6'"
          class="font-weight-bold text-gradient-primary px-3 rounded-lg"
          :style="{ marginLeft: xs ? '8px' : '16px' }"
        >
          {{ $route.name }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- Search - Hidden on XS, Compact on SM -->
        <v-text-field
          v-if="!xs"
          placeholder="Search..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          :style="{ maxWidth: sm ? '200px' : '280px', margin: '0 12px' }"
          hide-details
        ></v-text-field>

        <!-- Notifications -->
        <!-- <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <v-btn
              icon="mdi-bell-outline"
              variant="text"
              v-bind="props"
              size="small"
            >
              <v-badge
                :content="notificationCount"
                color="error"
                overlap
              ></v-badge>
            </v-btn>
          </template>
          <v-list :width="sm ? '280' : '300'">
            <v-list-item-title
              class="pa-4 text-semibold"
              :class="xs ? 'text-xs' : 'text-sm'"
            >
              Notifications
            </v-list-item-title>
            <v-divider></v-divider>
            <v-list-item
              v-for="notif in notifications"
              :key="notif.id"
              class="pa-3"
            >
              <v-icon :color="notif.color" size="small" class="mr-3">
                {{ notif.icon }}
              </v-icon>
              <div>
                <p
                  :class="xs ? 'text-xs' : 'text-sm'"
                  class="text-semibold mb-1"
                >
                  {{ notif.title }}
                </p>
                <p class="text-xs text-muted">{{ notif.time }}</p>
              </div>
            </v-list-item>
          </v-list>
        </v-menu> -->

        <!-- User Menu -->
      </v-app-bar>

      <!-- Main Content -->
      <v-main
        class="bg-light-gray"
        style="background-color: #f5f7fa; height: 100vh; overflow-y: auto"
      >
        <router-view
          :internalPincodes="allLocations"
          :vuetifyObject="vuetifyObject"
        ></router-view>
      </v-main>
    </v-layout>
  </v-app>
</template>

<script>
import { computed } from "vue";
import { useDisplay } from "vuetify";
import { logoutUser } from "../../utils/auth";
import { GetCurrentUserInfo } from "@/mixins/Extras/GetCurrentUserInfo";

export default {
  name: "LandingPage",
  setup() {
    const { xs, sm, md, lg, mdAndUp } = useDisplay();

    const drawerWidth = computed(() => {
      if (xs.value) return 200;
      if (sm.value) return 225;
      return 250;
    });

    const appBarHeight = computed(() => {
      if (xs.value) return 45;
      if (sm.value) return 55;
      return 60;
    });

    const avatarSize = computed(() => {
      if (xs.value) return 32;
      if (sm.value) return 36;
      return 40;
    });

    const iconSize = computed(() => {
      if (xs.value) return 16;
      return 20;
    });

    const helpIconSize = computed(() => {
      if (xs.value) return 20;
      if (sm.value) return 24;
      return 28;
    });

    return {
      xs,
      sm,
      md,
      lg,
      mdAndUp,
      drawerWidth,
      appBarHeight,
      avatarSize,
      iconSize,
      helpIconSize,
    };
  },
  data() {
    return {
      drawer: true,
      breadcrumbs: [{ title: "Dashboard", href: "#", disabled: false }],
      currentUser: {},
      notificationCount: 3,
      notifications: [
        {
          id: 1,
          title: "New Poll Created",
          time: "5 minutes ago",
          icon: "mdi-poll-box",
          color: "#667eea",
        },
        {
          id: 2,
          title: "Admin Added",
          time: "2 hours ago",
          icon: "mdi-account-plus",
          color: "#27ae60",
        },
        {
          id: 3,
          title: "System Update",
          time: "1 day ago",
          icon: "mdi-update",
          color: "#f39c12",
        },
      ],
      navItems: [
        { title: "Dashboard", path: "dashboard", icon: "mdi-view-dashboard" },
        { title: "Polls", path: "polls", icon: "mdi-poll" },
        { title: "Curators", path: "curators", icon: "mdi-account-group" },
        {
          title: "Requests",
          path: "requests",
          icon: "mdi-chart-box-plus-outline",
        },
        { title: "Flagged", path: "flagged", icon: "mdi-flag-remove" },
        {
          title: "Curator Requests",
          path: "curatorRequests",
          icon: "mdi-account-plus",
        },
      ],
      internalPincodes: [],
      internalUSZips: [],
      allLocations: [],
      vuetifyObject: {},
    };
  },
  mixins: [GetCurrentUserInfo],
  async mounted() {
    this.vuetifyObject = useDisplay();
    await this.fetchLocationData();
    try {
      this.currentUser = await this.GetCurrentUserInfoMethod(
        localStorage.getItem("user_email_id"),
      );
      if (!this.currentUser) {
        console.log(
          "User not found, clearing local storage and redirecting to login.",
        );
        localStorage.clear();
        this.$router.push("/login");
      }
    } catch (err) {
      console.log("Not logged in ❌");
    }
  },
  computed: {
    userEmail() {
      return this.currentUser.user_email_id || "Admin User";
    },
    userName() {
      return this.currentUser.user_name || "Admin User";
    },
    // Safely get the first letter of the email for the avatar
    userInitial() {
      if (this.userEmail) {
        return this.userEmail.charAt(0).toUpperCase();
      }
      return "A"; // Default to 'A' for Admin if no email is found
    },
  },
  methods: {
    async fetchLocationData() {
      try {
        // Fetch both files concurrently
        const [indiaResponse, usResponse] = await Promise.all([
          fetch("/pincode.csv"),
          fetch("/unitedStates.csv"),
        ]);

        if (!indiaResponse.ok || !usResponse.ok) {
          throw new Error("Failed to fetch one or more CSV files");
        }

        const indiaCsvText = await indiaResponse.text();
        const usCsvText = await usResponse.text();

        // Parse both datasets (this relies on parseCSV returning the data)
        this.internalPincodes = this.parseCSV(indiaCsvText) || [];
        this.internalUSZips = this.parseCSV(usCsvText) || [];

        // Create a unified array using your requested keys
        this.allLocations = [
          ...this.internalPincodes.map((item) => ({
            Country: "India",
            StateName: item.StateName,
            District: item.District,
            Pincode: item.Pincode,
          })),
          ...this.internalUSZips.map((item) => ({
            Country: "United States",
            StateName: item.state_name,
            District: item.county_name,
            Pincode: item.zip,
          })),
        ];

        console.log("Combined Data successfully loaded!");
      } catch (error) {
        console.error("Error loading CSV files:", error);
      }
    },
    parseCSV(csvText) {
      const lines = csvText.trim().split("\n");
      if (lines.length === 0) return [];

      // Extract headers and remove quotes
      const headers = lines[0]
        .split(",")
        .map((h) => h.replace(/^"|"$/g, "").trim());

      // Parse data rows
      const parsedData = lines.slice(1).map((line) => {
        // Regex splits by comma, but ignores commas inside double quotes
        const values = line
          .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
          .map((v) => (v ? v.replace(/^"|"$/g, "").trim() : ""));

        const rowObject = {};
        headers.forEach((header, index) => {
          rowObject[header] = values[index];
        });
        return rowObject;
      });

      // CRITICAL: You must return the data here so fetchLocationData can use it
      return parsedData;
    },
    // async fetchPincodeData() {
    //   try {
    //     // Points to /public/pincode.csv (Update filename to exactly match yours)
    //     const response = await fetch("/pincode.csv");
    //     if (!response.ok) throw new Error("Failed to fetch CSV");

    //     const csvText = await response.text();
    //     this.parseCSV(csvText);
    //   } catch (error) {
    //     console.error("Error loading CSV file:", error);
    //   }
    // },
    // parseCSV(csvText) {
    //   const lines = csvText.trim().split("\n");

    //   // Extract headers and remove quotes
    //   const headers = lines[0]
    //     .split(",")
    //     .map((h) => h.replace(/^"|"$/g, "").trim());

    //   // Parse data rows
    //   const parsedData = lines.slice(1).map((line) => {
    //     // Regex splits by comma, but ignores commas inside double quotes
    //     const values = line
    //       .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
    //       .map((v) => v.replace(/^"|"$/g, "").trim());

    //     const rowObject = {};
    //     headers.forEach((header, index) => {
    //       rowObject[header] = values[index];
    //     });
    //     return rowObject;
    //   });

    //   // Save the parsed JSON to our data variable
    //   this.internalPincodes = parsedData;
    // },
    isActive(path) {
      return this.$route.path === "/" + path;
    },
    async handleLogout() {
      try {
        await logoutUser();
        this.$router.push("/login");
      } catch (error) {
        console.error("Logout error:", error);
        // Still redirect to login even if logout fails
        this.$router.push("/login");
      }
    },
  },
  watch: {
    "$route.path": function (newPath) {
      // Update breadcrumb based on current route
      const pathMap = {
        "/dashboard": "Dashboard",
        "/polls": "Manage Polls",
        "/admins": "Manage Admins",
      };
      this.breadcrumbs = [
        { title: "Dashboard", href: "#", disabled: false },
        { title: pathMap[newPath] || "Page", href: "#", disabled: true },
      ];
    },
  },
};
</script>

<style scoped>
/* Navigation Drawer Styling */
.nav-drawer {
  background: #ffffff !important;
}

.nav-item {
  transition: all 0.2s ease;
  margin-bottom: 8px;
  margin-left: 8px;
  margin-right: 8px;
  border-radius: 8px;
}

.nav-item:hover {
  background-color: rgba(102, 126, 234, 0.08) !important;
}

.nav-item-active {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600;
}

.nav-item-active :deep(.v-list-item__content) {
  color: white !important;
}

.nav-item-active :deep(.v-icon) {
  color: white !important;
}

/* Utility Classes */
.text-semibold {
  font-weight: 600;
}

.text-muted {
  color: #888888;
}

.text-sm {
  font-size: 0.9rem;
}

.text-xsmall {
  font-size: 0.75rem;
}
</style>
